import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "react-query";
import { fetchContact, getMugshotImage } from "../../Redux/Actions/Contacts";
import Loader from "../../Components/Loader/Loader";
import "./details.scss";

const phoneEntries = (records) =>
  (Array.isArray(records) ? records : []).reduce((entries, record) => {
    if (record && typeof record === "object") {
      Object.entries(record).forEach(([type, number]) => {
        if (number) entries.push({ type, number: String(number) });
      });
    }
    return entries;
  }, []);

const formatPhone = (value = "") => {
  const original = String(value).trim();
  const digits = original.replace(/\D/g, "");
  const local = digits.length === 11 && digits.startsWith("1")
    ? digits.slice(1)
    : digits;
  if (local.length !== 10) return original;
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
};

const ContactDetails = (props) => {
  const contactId = props.match.params.id;
  const [contact, setContact] = useState(null);
  const [contactImage, setContactImage] = useState("");

  const { isLoading, isError, refetch } = useQuery(
    ["fetchContact", contactId],
    () => fetchContact(contactId),
    {
      onSuccess: (response) => {
        const records = response?.data;
        const match = Array.isArray(records)
          ? records.find((item) => String(item.id) === String(contactId))
          : records;
        setContact(match || null);
      },
      refetchOnWindowFocus: false,
    }
  );

  useQuery(["getMugshotImage", contactId], () => getMugshotImage(contactId), {
    onSuccess: (response) => setContactImage(response?.data || ""),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const fields = contact?.attributes || contact || {};
  const name = [fields.first_name, fields.last_name].filter(Boolean).join(" ") || "Contact Details";
  const phones = phoneEntries(fields.phone_and_type);
  const addresses = Array.isArray(fields.addresses_and_type) ? fields.addresses_and_type : [];

  return (
    <main className="ds-contact-detail-page">
      <Link to="/contacts" className="ds-contact-detail__back">← Contacts</Link>
      {isLoading ? <Loader /> : isError ? (
        <div className="ds-contact-detail__empty">Unable to load contact. <button type="button" onClick={() => refetch()}>Retry</button></div>
      ) : !contact ? (
        <div className="ds-contact-detail__empty">Contact not found.</div>
      ) : (
        <>
          <section className="ds-contact-detail__summary">
            <div className="ds-contact-detail__identity">
              <div>
                <div className="ds-contact-detail__eyebrow">CONTACT PROFILE</div>
                <h1>{name}</h1>
                {fields.affiliation && <p>{fields.affiliation}</p>}
              </div>
              {contactImage && (
                <img src={contactImage} alt={`${name} profile`} className="ds-contact-detail__photo" />
              )}
            </div>
          </section>
          <section className="ds-contact-detail__card">
            <h2>Contact Information</h2>
            {fields.email && (
              <div className="ds-contact-detail__field">
                <span>Email</span>
                <a href={`mailto:${fields.email}`}>{fields.email}</a>
              </div>
            )}
            {phones.map(({ type, number }, index) => (
              <div className="ds-contact-detail__field" key={`${type}-${index}`}>
                <span>{type}</span>
                <a href={`tel:${number.replace(/[^\d+]/g, "")}`}>{formatPhone(number)}</a>
              </div>
            ))}
            {!fields.email && !phones.length && <p className="ds-contact-detail__muted">No contact information available.</p>}
          </section>
          {addresses.length > 0 && (
            <section className="ds-contact-detail__card">
              <h2>Addresses</h2>
              {addresses.map((address, index) => {
                const line1 = [address.street1, address.street2].filter(Boolean).join(" ");
                const line2 = [address.city, address.state, address.postal_code].filter(Boolean).join(", ");
                const fullAddress = [line1, line2].filter(Boolean).join(", ");
                return (
                  <div className="ds-contact-detail__field" key={`${address.type || "address"}-${index}`}>
                    <span>{address.type || "Address"}</span>
                    <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`}
                      target="_blank" rel="noopener noreferrer">
                      {line1}{line1 && line2 ? ", " : ""}{line2} ↗
                    </a>
                  </div>
                );
              })}
            </section>
          )}
        </>
      )}
    </main>
  );
};

export default ContactDetails;
